import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Plus, Trash2, Send, Square, Globe, ChevronDown, Copy, Check,
  MessageSquare, Pencil, Zap, CheckSquare, Calendar, Users, FileText, Mail, X
} from 'lucide-react';
import { useUserId, useUserEmail } from '../context/UserAuthContext';

const API = window.location.origin;

// ─── Markdown Code Block ────────────────────────────────────────────────────
function CodeBlock({ language, children }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative my-3 rounded-lg overflow-hidden border border-[#3a3a3a]">
      <div className="flex items-center justify-between px-4 py-2 bg-[#1e1e1e] border-b border-[#3a3a3a]">
        <span className="text-xs text-zinc-400 font-mono">{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
          data-testid="copy-code-btn"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        style={oneDark}
        language={language || 'text'}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: 0, background: '#141414', fontSize: '13px' }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

// ─── Message Renderer ────────────────────────────────────────────────────────
function MessageContent({ content, isStreaming }) {
  return (
    <div className="prose prose-invert max-w-none text-[14.5px] leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            if (!inline && match) {
              return (
                <CodeBlock language={match[1]}>
                  {String(children).replace(/\n$/, '')}
                </CodeBlock>
              );
            }
            return (
              <code className="bg-[#2a2a2a] text-emerald-300 px-1.5 py-0.5 rounded text-[13px] font-mono" {...props}>
                {children}
              </code>
            );
          },
          table({ children }) {
            return (
              <div className="overflow-x-auto my-3">
                <table className="w-full text-sm border-collapse">{children}</table>
              </div>
            );
          },
          th({ children }) {
            return <th className="border border-[#3a3a3a] px-3 py-2 bg-[#1e1e1e] text-left font-semibold">{children}</th>;
          },
          td({ children }) {
            return <td className="border border-[#3a3a3a] px-3 py-2">{children}</td>;
          },
          blockquote({ children }) {
            return <blockquote className="border-l-4 border-blue-500 pl-4 my-3 text-zinc-400 italic">{children}</blockquote>;
          },
          hr() {
            return <hr className="border-[#3a3a3a] my-4" />;
          },
          a({ href, children }) {
            return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{children}</a>;
          },
          ul({ children }) {
            return <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>;
          },
          h1({ children }) { return <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>; },
          h2({ children }) { return <h2 className="text-lg font-semibold mt-4 mb-2">{children}</h2>; },
          h3({ children }) { return <h3 className="text-base font-semibold mt-3 mb-1">{children}</h3>; },
          p({ children }) { return <p className="my-2">{children}</p>; },
        }}
      >
        {content}
      </ReactMarkdown>
      {isStreaming && (
        <span className="inline-block w-0.5 h-4 bg-emerald-400 ml-0.5 animate-pulse" />
      )}
    </div>
  );
}

const MODELS = [
  { id: 'gpt', label: 'GPT-6', provider: 'openai' },
  { id: 'claude', label: 'Claude', provider: 'anthropic' },
  { id: 'gemini', label: 'Gemini', provider: 'gemini' },
];

const TOOL_CATEGORIES = [
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Read tasks, create new ones, mark as done',
    icon: CheckSquare,
    keywords: ['task', 'todo', 'create task', 'mark done'],
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    description: 'Check your schedule, create meetings',
    icon: Calendar,
    keywords: ['meeting', 'schedule', 'today', 'this week'],
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    id: 'contacts',
    label: 'Contacts',
    description: 'Search contacts by name, email, or phone',
    icon: Users,
    keywords: ['find', 'search', 'email of', 'phone of'],
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Browse your uploaded files and docs',
    icon: FileText,
    keywords: ['document', 'file', 'doc'],
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Read inbox, send emails directly from chat',
    icon: Mail,
    keywords: ['email', 'inbox', 'send email'],
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
  },
];

export default function ChatPage() {
  const userId = useUserId();
  const userEmail = useUserEmail();

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [model, setModel] = useState('gpt');
  const [webSearch, setWebSearch] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [enabledTools, setEnabledTools] = useState(() => {
    try {
      const saved = localStorage.getItem('operatr_enabled_tools');
      return saved ? JSON.parse(saved) : { tasks: true, calendar: true, contacts: true, documents: true, email: true };
    } catch { /* ignored */ }
    return { tasks: true, calendar: true, contacts: true, documents: true, email: true };
  });

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const sessionId = useRef(`chat-${Date.now()}`);

  // ── Tool toggle ─────────────────────────────────────────────────────────
  const toggleTool = (toolId) => {
    setEnabledTools(prev => {
      const next = { ...prev, [toolId]: !prev[toolId] };
      try { localStorage.setItem('operatr_enabled_tools', JSON.stringify(next)); } catch { /* ignored */ }
      return next;
    });
  };

  // ── Load conversations ──────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/api/ai-assistant/conversations/list?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (_e) { /* ignore fetch error */ }
  }, [userId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Load messages for active conversation ────────────────────────────────
  useEffect(() => {
    if (!activeConvId) { setMessages([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/ai-assistant/conversations/${activeConvId}/messages`);
        if (res.ok) setMessages(await res.json());
      } catch (_e) { /* ignore */ }
    })();
  }, [activeConvId]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── New chat ─────────────────────────────────────────────────────────────
  const startNewChat = async () => {
    if (!userId) return;
    const res = await fetch(
      `${API}/api/ai-assistant/conversations/new?user_id=${userId}&model=${model}`,
      { method: 'POST' }
    );
    if (res.ok) {
      const data = await res.json();
      await loadConversations();
      setActiveConvId(data.id);
      setMessages([]);
      sessionId.current = `chat-${data.id}`;
    }
  };

  // ── Delete conversation ──────────────────────────────────────────────────
  const deleteConv = async (id, e) => {
    e.stopPropagation();
    await fetch(`${API}/api/ai-assistant/conversations/${id}`, { method: 'DELETE' });
    if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
    await loadConversations();
  };

  // ── Rename conversation ──────────────────────────────────────────────────
  const saveRename = async (id) => {
    if (!renameVal.trim()) return;
    await fetch(`${API}/api/ai-assistant/conversations/${id}/title?title=${encodeURIComponent(renameVal)}`, { method: 'PATCH' });
    setRenamingId(null);
    await loadConversations();
  };

  // ── Stop streaming ────────────────────────────────────────────────────────
  const stopStreaming = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    // Ensure there's an active conversation
    let convId = activeConvId;
    if (!convId) {
      const res = await fetch(
        `${API}/api/ai-assistant/conversations/new?user_id=${userId}&model=${model}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        convId = data.id;
        setActiveConvId(data.id);
        sessionId.current = `chat-${data.id}`;
        await loadConversations();
      }
    }

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const assistantMsgId = `stream-${Date.now()}`;
    const assistantMsg = { id: assistantMsgId, role: 'assistant', content: '', timestamp: new Date().toISOString() };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setStreamingMsgId(assistantMsgId);
    setInput('');
    setStreaming(true);

    // Auto-rename conversation from first message
    if (conversations.find(c => c.id === convId)?.title === 'New Chat') {
      const shortTitle = text.length > 40 ? text.slice(0, 37) + '...' : text;
      fetch(`${API}/api/ai-assistant/conversations/${convId}/title?title=${encodeURIComponent(shortTitle)}`, { method: 'PATCH' })
        .then(() => loadConversations());
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/api/ai-assistant/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: text,
          model,
          conversation_id: convId,
          user_id: userId,
          enable_web_search: webSearch || null,
          enabled_tools: Object.entries(enabledTools).filter(([, v]) => v).map(([k]) => k),
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'token') {
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantMsgId ? { ...m, content: m.content + evt.content } : m
                )
              );
            } else if (evt.type === 'tool_call') {
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantMsgId ? { ...m, toolLabel: evt.label } : m
                )
              );
            } else if (evt.type === 'tool_done') {
              setTimeout(() => {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsgId ? { ...m, toolLabel: null } : m
                  )
                );
              }, 600);
            } else if (evt.type === 'done') {
              setStreaming(false);
              setStreamingMsgId(null);
              await loadConversations();
            } else if (evt.type === 'error') {
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantMsgId ? { ...m, content: `Error: ${evt.message}` } : m
                )
              );
              setStreaming(false);
              setStreamingMsgId(null);
            }
          } catch (_e) { /* ignore malformed SSE */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: 'Connection error. Please try again.' } : m
          )
        );
      }
      setStreaming(false);
      setStreamingMsgId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const activeModel = MODELS.find(m => m.id === model) || MODELS[0];
  const activeConv = conversations.find(c => c.id === activeConvId);

  return (
    <div className="flex bg-[#0f0f0f] text-white overflow-hidden" style={{ height: 'calc(100vh - 0px)' }} data-testid="chat-page">

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <div
        className={`flex flex-col bg-[#111111] border-r border-[#232323] transition-all duration-200 ${
          sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'
        }`}
      >
        {/* New Chat */}
        <div className="p-3 border-b border-[#232323]">
          <button
            onClick={startNewChat}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium"
            data-testid="new-chat-btn"
          >
            <Plus size={15} />
            New chat
          </button>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto py-2">
          {conversations.length === 0 && (
            <p className="text-zinc-500 text-xs text-center mt-6 px-4">No conversations yet.<br/>Start a new chat!</p>
          )}
          {conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => setActiveConvId(conv.id)}
              className={`group flex items-center gap-2 px-3 py-2.5 mx-2 rounded-lg cursor-pointer transition-colors ${
                activeConvId === conv.id ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
              data-testid={`conversation-item-${conv.id}`}
            >
              <MessageSquare size={13} className="text-zinc-400 shrink-0" />
              {renamingId === conv.id ? (
                <input
                  autoFocus
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveRename(conv.id); if (e.key === 'Escape') setRenamingId(null); }}
                  onBlur={() => saveRename(conv.id)}
                  className="flex-1 bg-transparent border-b border-zinc-500 text-xs text-white outline-none min-w-0"
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span className="flex-1 text-xs text-zinc-300 truncate">{conv.title || 'New Chat'}</span>
              )}
              <div className="hidden group-hover:flex items-center gap-1">
                <button
                  onClick={e => { e.stopPropagation(); setRenamingId(conv.id); setRenameVal(conv.title || ''); }}
                  className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-white"
                  data-testid="rename-conv-btn"
                >
                  <Pencil size={11} />
                </button>
                <button
                  onClick={e => deleteConv(conv.id, e)}
                  className="p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400"
                  data-testid="delete-conv-btn"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* User info */}
        <div className="p-3 border-t border-[#232323]">
          <p className="text-xs text-zinc-500 truncate">{userEmail}</p>
        </div>
      </div>

      {/* ── Main Chat ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#232323] bg-[#0f0f0f]">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
            data-testid="toggle-sidebar-btn"
          >
            <MessageSquare size={16} />
          </button>

          {/* Model switcher */}
          <div className="relative">
            <button
              onClick={() => setShowModelMenu(!showModelMenu)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium"
              data-testid="model-switcher-btn"
            >
              {activeModel.label}
              <ChevronDown size={14} className="text-zinc-400" />
            </button>
            {showModelMenu && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl z-50 overflow-hidden">
                {MODELS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => { setModel(m.id); setShowModelMenu(false); }}
                    className={`w-full text-left px-3 py-2.5 text-sm hover:bg-white/10 transition-colors ${model === m.id ? 'text-emerald-400' : 'text-zinc-300'}`}
                    data-testid={`model-option-${m.id}`}
                  >
                    {m.label}
                    {model === m.id && <span className="ml-2 text-xs">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Web search toggle */}
          <button
            onClick={() => setWebSearch(!webSearch)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              webSearch ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
            data-testid="web-search-toggle"
          >
            <Globe size={14} />
            Web
          </button>

          <div className="flex-1" />
          <button
            onClick={() => setToolsOpen(!toolsOpen)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              toolsOpen ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
            data-testid="tools-panel-toggle"
            title="OPERATR Tools"
          >
            <Zap size={14} />
            <span className="hidden sm:inline">Tools</span>
            {Object.values(enabledTools).filter(Boolean).length > 0 && (
              <span className="ml-0.5 text-[10px] text-zinc-500">
                {Object.values(enabledTools).filter(Boolean).length}
              </span>
            )}
          </button>
          <span className="text-xs text-zinc-600 hidden sm:block ml-2">Enter to send · Shift+Enter newline</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                <MessageSquare size={24} className="text-zinc-400" />
              </div>
              <h2 className="text-xl font-semibold text-zinc-200 mb-2">
                {activeConv ? activeConv.title : 'How can I help?'}
              </h2>
              <p className="text-zinc-500 text-sm max-w-md">
                Ask me anything — I can help with tasks, research, writing, code, and more.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                {[
                  'What tasks do I have this week?',
                  'Show my upcoming meetings',
                  'Check my inbox',
                  'Send an email to John about the meeting tomorrow',
                ].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => { setInput(suggestion); textareaRef.current?.focus(); }}
                    className="px-4 py-3 text-sm text-left rounded-xl bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors border border-white/5"
                    data-testid="suggestion-btn"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-6">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  data-testid={`message-${msg.role}`}
                >
                  {/* Avatar */}
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    msg.role === 'user'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-[#1e1e1e] text-zinc-300 border border-[#2a2a2a]'
                  }`}>
                    {msg.role === 'user' ? (userEmail?.[0] || 'U').toUpperCase() : 'AI'}
                  </div>

                  {/* Bubble */}
                  <div className={`flex-1 ${msg.role === 'user' ? 'flex justify-end' : ''}`}>
                    {msg.role === 'user' ? (
                      <div className="max-w-[80%] px-4 py-2.5 rounded-2xl bg-[#1e1e1e] text-zinc-200 text-[14.5px] leading-relaxed whitespace-pre-wrap">
                        {msg.content}
                      </div>
                    ) : (
                      <div className="text-zinc-200">
                        {/* Tool call indicator */}
                        {msg.toolLabel && (
                          <div className="flex items-center gap-2 mb-2 text-sm text-zinc-400" data-testid="tool-call-indicator">
                            <span className="flex gap-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{animationDelay:'0ms'}}/>
                              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{animationDelay:'150ms'}}/>
                              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{animationDelay:'300ms'}}/>
                            </span>
                            <span className="italic">{msg.toolLabel}</span>
                          </div>
                        )}
                        {/* Waiting dots when no content yet and no tool label */}
                        {!msg.content && !msg.toolLabel && streamingMsgId === msg.id && (
                          <div className="flex gap-1 py-1">
                            <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{animationDelay:'0ms'}}/>
                            <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{animationDelay:'150ms'}}/>
                            <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{animationDelay:'300ms'}}/>
                          </div>
                        )}
                        {msg.content && (
                          <MessageContent
                            content={msg.content}
                            isStreaming={streamingMsgId === msg.id}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Input bar */}
        <div className="px-4 py-4 border-t border-[#232323] bg-[#0f0f0f]">
          <div className="max-w-3xl mx-auto">
            <form onSubmit={sendMessage} className="relative">
              <div className="flex items-end gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl px-4 py-3 focus-within:border-[#3a3a3a] transition-colors">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message..."
                  rows={1}
                  disabled={streaming}
                  className="flex-1 bg-transparent text-zinc-200 placeholder-zinc-600 resize-none outline-none text-sm leading-relaxed max-h-40 overflow-y-auto"
                  style={{ minHeight: '24px' }}
                  data-testid="chat-input"
                />
                <div className="flex items-center gap-2 shrink-0">
                  {streaming ? (
                    <button
                      type="button"
                      onClick={stopStreaming}
                      className="w-8 h-8 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center transition-colors"
                      data-testid="stop-btn"
                    >
                      <Square size={14} fill="currentColor" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!input.trim()}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        input.trim()
                          ? 'bg-white text-black hover:bg-zinc-200'
                          : 'bg-white/10 text-zinc-600 cursor-not-allowed'
                      }`}
                      data-testid="send-btn"
                    >
                      <Send size={14} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-center text-[11px] text-zinc-600 mt-2">
                AI can make mistakes. Consider checking important information.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* ── Tools Panel (right) ────────────────────────────────────────────── */}
      <div className={`shrink-0 border-l border-[#232323] bg-[#0d0d0d] flex flex-col transition-all duration-200 overflow-hidden ${
        toolsOpen ? 'w-72' : 'w-0'
      }`}>
        {toolsOpen && (
          <>
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#232323]">
              <div className="flex items-center gap-2">
                <Zap size={15} className="text-emerald-400" />
                <span className="text-sm font-semibold text-zinc-200">OPERATR Tools</span>
              </div>
              <button
                onClick={() => setToolsOpen(false)}
                className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-white transition-colors"
                data-testid="close-tools-btn"
              >
                <X size={14} />
              </button>
            </div>

            {/* Connected user */}
            <div className="px-4 py-3 border-b border-[#1a1a1a]">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Connected as</p>
              <p className="text-xs text-zinc-300 font-medium truncate">{userEmail || 'Loading...'}</p>
              <p className="text-[10px] text-zinc-600 mt-0.5">Data is personal to your account</p>
            </div>

            {/* Tool toggles */}
            <div className="flex-1 overflow-y-auto py-3 px-3 space-y-2">
              {TOOL_CATEGORIES.map(tool => {
                const Icon = tool.icon;
                const active = enabledTools[tool.id] !== false;
                return (
                  <button
                    key={tool.id}
                    onClick={() => toggleTool(tool.id)}
                    className={`w-full flex items-start gap-3 px-3 py-3 rounded-xl border transition-all text-left ${
                      active
                        ? 'bg-white/5 border-white/10 hover:bg-white/8'
                        : 'bg-transparent border-transparent hover:bg-white/3 opacity-50'
                    }`}
                    data-testid={`tool-toggle-${tool.id}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${active ? tool.bg : 'bg-white/5'}`}>
                      <Icon size={15} className={active ? tool.color : 'text-zinc-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold ${active ? 'text-zinc-200' : 'text-zinc-500'}`}>{tool.label}</span>
                        {/* Toggle pill */}
                        <div className={`shrink-0 w-8 h-4 rounded-full transition-colors relative ${active ? 'bg-emerald-500' : 'bg-zinc-700'}`}>
                          <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${active ? 'left-4' : 'left-0.5'}`} />
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{tool.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer note */}
            <div className="px-4 py-3 border-t border-[#1a1a1a]">
              <p className="text-[10px] text-zinc-600 leading-relaxed">
                When enabled, GPT-6 automatically fetches your real data when you ask about these topics.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
